import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { FaEdit, FaPlus, FaShoppingCart, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;


const FactoryBranch = () => {
  const [branches, setBranches] = useState([]);
  const [branchName, setBranchName] = useState("");
  const [description, setDescription] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // New state for purchase modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  const navigate = useNavigate();

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = userData?.email;
  const FactoryCode = userData?.FactoryCode;

  // Fetch branch data
  const fetchBranchData = useCallback(async () => {
    if (!FactoryCode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-factory-branches?FactoryCode=${FactoryCode}`);
      const data = await response.json();

      if (response.ok) {
        setBranches(data);
      } else {
        notify("Error fetching branches.", "error");
      }
    } catch (error) {
      console.error("Error fetching branch data:", error);
      notify("Error fetching branch data. Please check your connection.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [FactoryCode]);

  useEffect(() => {
    fetchBranchData();
  }, [fetchBranchData]);

  // Form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "branchName") setBranchName(value);
    if (name === "description") setDescription(value);
  };

  // Reset form and modal state
  const resetForm = () => {
    setBranchName("");
    setDescription("");
    setIsEditing(false);
    setEditId(null);
  };

  // Open modal for creating branch
  const openCreateModal = async () => {
    try {
      // Get branch counts and limits
      const response = await fetch(`${BASE_URL}/factory-branch-limit?FactoryCode=${FactoryCode}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check branch limit');
      }

      // Check if branch limit is reached
      if (data.branchCount >= data.branchQty) {
        setShowPurchaseModal(true);
      } else {
        resetForm();
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error checking branch limit:', error);
    }
  };

  // Handle Buy Now action
  const handleBuyNow = () => {
    setShowPurchaseModal(false);
    // Navigate to the pricing/upgrade page
    navigate('/pricing', { 
      state: { 
        from: 'branch',
        FactoryCode: FactoryCode
      } 
    });
  };

  // Handle branch creation
  const handleCreateBranch = async () => {
    if (!branchName || !description) {
      notify("Please fill in all fields", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/add-factory-branch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ branchName, description, emailAddress: userEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        notify("Branch created successfully!");
        resetForm();
        setShowModal(false);
        fetchBranchData();
      } else {
        if (data.error === "Branch creation limit reached") {
          setShowModal(false);
          setShowPurchaseModal(true);
        } else {
          notify(data.error || "Error creating branch.", "error");
        }
      }
    } catch (error) {
      console.error("Error creating branch:", error);
      notify("Error creating branch. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Edit
  const handleEdit = (branch) => {
    setIsEditing(true);
    setEditId(branch.id);
    setBranchName(branch.branchName);
    setDescription(branch.description);
    setShowModal(true);
  };

  // Handle Update
  const handleUpdateBranch = async () => {
    if (!branchName || !description) {
      notify("Please fill in all fields", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/update-factory-branch/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ branchName, description }),
      });

      const data = await response.json();

      if (response.ok) {
        notify("Branch updated successfully!");
        resetForm();
        setShowModal(false);
        fetchBranchData();
      } else {
        notify(data.error || "Error updating branch.", "error");
      }
    } catch (error) {
      console.error("Error updating branch:", error);
      notify("Error updating branch. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/delete-factory-branch/${branchToDelete}`, {
        method: "DELETE",
      });
      const data = await response.json();
      
      if (response.ok) {
        notify(data.message || "Branch deleted successfully!");
        fetchBranchData();
        setShowDeleteModal(false);
      } else {
        notify(data.error || "Error deleting branch.", "error");
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting branch:", error);
      notify("Error deleting branch. Please try again.", "error");
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Define columns for react-table
  const columns = React.useMemo(
    () => [
      {
        Header: "Branch Name",
        accessor: "branchName",
      },
      {
        Header: "Description",
        accessor: "description",
        // Limit description text on small devices
        Cell: ({ value }) => (
          <div className="description-cell">
            {window.innerWidth < 768 && value.length > 50
              ? `${value.substring(0, 50)}...`
              : value}
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (

          <div className="d-flex gap-2 justify-content-center">
          <Button 
            variant="outline-warning" 
            size="sm" 
            onClick={() => handleEdit(row.original)}
            disabled={isLoading}
            className="d-flex align-items-center"
          >
            <FaEdit className="me-1" /> Edit
          </Button>
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => {
              setBranchToDelete(row.original.id);
              setShowDeleteModal(true);
            }}
            disabled={isLoading}
            className="d-flex align-items-center"
          >
            <FaTrash className="me-1" /> Delete
          </Button>
        </div>




        ),
      },
    ],
    []
  );

  // Use react-table hook
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: branches,
  });

  return (
    <Container fluid className="py-3">
        <ToastContainer />
      <Row className="justify-content-center">
        <Col xs={12} lg={10} xl={9}>
          <Card className="shadow-lg border-0 rounded-lg mb-4">
            <Card.Header className="bg-primary text-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0 fs-4">Your Branches</h3>
                  <p className="text-white-50 mb-0 d-none d-sm-block">Manage your branches</p>
                </div>
                <Button 
                  variant="light" 
                  className="d-flex align-items-center" 
                  onClick={openCreateModal}
                  disabled={isLoading}
                >
                  <FaPlus className="me-2" /> <span className="d-none d-sm-inline">Add Branch</span>
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="px-2 px-sm-3 px-md-4">
              {/* Enhanced Loading indicator */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-bold mb-1">Loading Branches</p>
                  <p className="text-muted">Please wait while we fetch your branch data...</p>
                </div>
              )}

              {/* Only show content when not loading */}
              {!isLoading && (
                <>
                  {/* Mobile View - Card List */}
                  <div className="d-md-none">
                    {branches.length === 0 ? (
                      <p className="text-center py-3">No branches found. Add your first branch.</p>
                    ) : (
                      branches.map((branch) => (
                        <Card key={branch.id} className="mb-3 branch-card">
                          <Card.Body>
                            <Card.Title>{branch.branchName}</Card.Title>
                            <Card.Text>{branch.description}</Card.Text>
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleEdit(branch)}
                                aria-label="Edit branch"
                              >
                                <FaEdit /> Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  setBranchToDelete(branch.id);
                                  setShowDeleteModal(true);
                                }}
                                aria-label="Delete branch"
                              >
                                <FaTrash /> Delete
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </div>

                  {/* Desktop View - Table */}
                  <div className="table-responsive d-none d-md-block">
                    <Table striped bordered hover responsive {...getTableProps()}>
                      <thead>
                        {headerGroups.map((headerGroup, idx) => (
                          <tr key={idx} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, colIdx) => (
                              <th key={colIdx} {...column.getHeaderProps()}>
                                {column.render("Header")}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center py-3">
                              No branches found. Add your first branch.
                            </td>
                          </tr>
                        ) : (
                          rows.map((row, rowIdx) => {
                            prepareRow(row);
                            return (
                              <tr key={rowIdx} {...row.getRowProps()}>
                                {row.cells.map((cell, cellIdx) => (
                                  <td key={cellIdx} {...cell.getCellProps()}>
                                    {cell.render("Cell")}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal for adding/editing branch */}
      <Modal 
        show={showModal} 
        onHide={() => {
          setShowModal(false);
          resetForm();
        }} 
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Branch" : "Add New Branch"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="branchName">
              <Form.Label>Branch Name</Form.Label>
              <Form.Control
                type="text"
                name="branchName"
                placeholder="Enter branch name"
                value={branchName}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group controlId="description" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={3}
                placeholder="Enter branch description"
                value={description}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={isEditing ? handleUpdateBranch : handleCreateBranch}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              isEditing ? 'Update Branch' : 'Create Branch'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)} 
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this branch? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              'Delete Branch'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Purchase Modal for Branch Limit */}
      <Modal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upgrade Your Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <FaShoppingCart className="text-primary" size={50} />
          </div>
          <h4 className="text-center mb-3">Branch Creation Limit Reached</h4>
          <p className="text-center">
            You've reached the maximum number of branches allowed in your current plan. 
            Upgrade now to create unlimited branches and access premium features.
          </p>
          <div className="bg-light p-3 rounded mt-3">
            <h5>Premium Plan Benefits:</h5>
            <ul className="mt-2">
              <li>Unlimited branch creation</li>
              <li>Advanced analytics</li>
              <li>Priority support</li>
              <li>Additional customization options</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowPurchaseModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleBuyNow}
            className="d-flex align-items-center"
          >
            <FaShoppingCart className="me-2" /> Buy Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add CSS for better responsiveness */}
      <style jsx>{`
        .description-cell {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        @media (max-width: 768px) {
          .branch-card {
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }
          
          .branch-card:hover {
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
          }
        }
        
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Fadeout animation for when loading completes */
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .loading-fadeout {
          animation: fadeOut 0.5s ease-out forwards;
        }
      `}</style>
    </Container>
  );
};

export default FactoryBranch;